import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastmessageService } from 'src/app/toastmessage.service';
import { MessService } from '../mess.service';
import moment from 'moment';
import { CellClickedEvent, ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { NotSubmittedViewDialogComponent } from '../not-submitted-view-dialog/not-submitted-view-dialog.component';
import {
  HolidayCloseDialogComponent,
  HolidayCloseDialogData,
  MessHolidayClosePayload,
} from '../holiday-close-dialog/holiday-close-dialog.component';

export interface PeriodicElement2 {
  messBuildingId: any;
  messBuildingName: any;
  daysheetDate: any;
  status: any;
  studentCount: any;
  employeeCount: any;
  subStaffCount: any;
  visitorsCount: any;
}

type DoViewMode = 'pending' | 'monthly';

interface MonthlySummary {
  closed: number;
  pending: number;
  holiday: number;
  notSubmitted: number;
}

@Component({
  selector: 'app-mess-do-details',
  templateUrl: './mess-do-details.component.html',
  styleUrls: ['./mess-do-details.component.scss']
})
export class MessDoDetailsComponent implements OnInit {
  @ViewChild('searchInputcampus') searchInputcampus!: ElementRef;
  @ViewChild('monthlyGridWrap') monthlyGridWrap?: ElementRef<HTMLElement>;
  gridApi!: GridApi;
  quickFilter: string = '';

  /** Pending list (default) vs month-wise overview grid */
  viewMode: DoViewMode = 'monthly';

  selectedMonth = moment();
  monthLabel = '';
  overviewPickerOpen = false;
  /** Picker panel: year grid or month grid (no native dropdowns). */
  overviewPickerView: 'year' | 'month' = 'month';
  pickerMonth = moment().month();
  pickerYear = moment().year();
  yearPageStart = moment().year() - 5;
  readonly monthPickerOptions = moment.monthsShort().map((label, value) => ({ value, label }));
  monthlySummary: MonthlySummary = { closed: 0, pending: 0, holiday: 0, notSubmitted: 0 };
  totalCampuses = 0;
  /** Trend badge arrow from API counts: 0 = down, greater than 0 = up. */
  summaryTrendUp = {
    closed: false,
    pending: false,
    holiday: false,
    notSubmitted: false,
    totalCampuses: false,
  };

  monthlyColDefs: ColDef[] = [];
  /** Days in the selected month (28, 29, 30, or 31). */
  monthlyDayCount = 31;
  /** Days shown in the grid (current month: today → 1; other months: last day → 1). */
  monthlyVisibleDayCount = 31;

  /** Pinned campus (resizable); day columns use flex in remaining width. */
  private readonly monthlyCampusDefaultWidth = 300;
  private readonly monthlyCampusMinWidth = 220;
  private readonly monthlyCampusMaxWidth = 480;
  private readonly monthlySummaryWidth = 118;
  /** Minimum width per day column so headers stay readable (e.g. 1 / Fri). */
  private readonly monthlyDayMinWidth = 26;

  monthlyGridOptions: GridOptions = {
    suppressHorizontalScroll: true,
  };

  monthlyDefaultColDef: ColDef = {
    menuTabs: [],
    suppressHeaderMenuButton: true,
    sortable: false,
    filter: false,
    resizable: false,
    flex: 0,
  };

  constructor(
    private service: MessService,
    private spinner: NgxSpinnerService,
    private toast: ToastmessageService,
    private router: Router,
    private matDialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.syncPickerFromSelectedMonth();
    this.syncMonthLabel();
    this.loadMonthlyReport();
    // this.pendingList();
  }

  get activeColDefs(): ColDef[] {
    return this.viewMode === 'monthly' ? this.monthlyColDefs : this.colDefs;
  }

  get activeDefaultColDef(): ColDef {
    return this.viewMode === 'monthly' ? this.monthlyDefaultColDef : this.defaultColDef;
  }

  get activeGridOptions(): GridOptions | undefined {
    return this.viewMode === 'monthly' ? this.monthlyGridOptions : undefined;
  }

  get activeRowData(): any[] {
    return this.rowData;
  }

  get summaryTotal(): number {
    const s = this.monthlySummary;
    return (s.closed ?? 0) + (s.pending ?? 0) + (s.holiday ?? 0) + (s.notSubmitted ?? 0);
  }

  summaryPercent(value: number): string {
    const total = this.summaryTotal;
    if (!total) {
      return '0%';
    }
    return ((Number(value ?? 0) / total) * 100).toFixed(1) + '%';
  }

  isSummaryTrendUp(key: keyof typeof this.summaryTrendUp): boolean {
    return !!this.summaryTrendUp[key];
  }

  /** Arrow direction from current API response only (zero = down). */
  private updateSummaryTrendsFromResponse(summary: MonthlySummary, totalCampuses: number): void {
    this.summaryTrendUp = {
      closed: Number(summary.closed ?? 0) > 0,
      pending: Number(summary.pending ?? 0) > 0,
      holiday: Number(summary.holiday ?? 0) > 0,
      notSubmitted: Number(summary.notSubmitted ?? 0) > 0,
      totalCampuses: Number(totalCampuses ?? 0) > 0,
    };
  }

  setViewMode(mode: DoViewMode): void {
    if (this.viewMode === mode) {
      return;
    }
    this.clearGridRowData();
    this.viewMode = mode;
    this.quickFilter = '';
    this.refreshGridColumns();
    if (mode === 'monthly') {
      this.buildMonthlyColumnDefs();
      this.loadMonthlyReport();
    } else {
      this.pendingList();
    }
  }

  /** Avoid showing previous tab's rows while the next API call is in flight. */
  private clearGridRowData(): void {
    this.rowData = [];
    this.gridApi?.setGridOption('rowData', []);
    this.gridApi?.showLoadingOverlay();
  }

  private refreshGridColumns(): void {
    setTimeout(() => {
      if (!this.gridApi) {
        return;
      }
      this.gridApi.setGridOption('columnDefs', this.activeColDefs);
      this.gridApi.setGridOption('defaultColDef', this.activeDefaultColDef);
      this.gridApi.resetColumnState();
    });
  }

  private applyMonthlyGridLayout(): void {
    setTimeout(() => {
      if (!this.gridApi || this.viewMode !== 'monthly') {
        return;
      }
      this.rebuildMonthlyColumnsToFitViewport();
    });
  }

  /** Rebuild day column widths so all days (28–31) fit without horizontal scroll. */
  private rebuildMonthlyColumnsToFitViewport(): void {
    this.buildMonthlyColumnDefs();
    if (!this.gridApi) {
      return;
    }
    this.gridApi.setGridOption('columnDefs', this.monthlyColDefs);
    this.gridApi.setGridOption('defaultColDef', this.monthlyDefaultColDef);
    this.gridApi.resetColumnState();
  }

  /** Keeps user-resized campus width when month columns are rebuilt. */
  private getMonthlyCampusWidth(): number {
    const col = this.gridApi?.getColumn('messBuildingName');
    const w = col?.getActualWidth();
    if (w != null && Number.isFinite(w) && w >= this.monthlyCampusMinWidth) {
      return Math.min(this.monthlyCampusMaxWidth, w);
    }
    return this.monthlyCampusDefaultWidth;
  }

  /** Align selected month with API label (e.g. "May 2026") so day count is 30 or 31. */
  private syncSelectedMonthFromReport(monthText: string): void {
    const parsed = moment(String(monthText ?? '').trim(), 'MMM YYYY', true);
    if (parsed.isValid()) {
      this.selectedMonth = parsed.clone().startOf('month');
      this.syncPickerFromSelectedMonth();
      this.syncMonthLabel();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.overviewPickerOpen) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target && !target.closest('.overview-date-picker')) {
      this.overviewPickerOpen = false;
    }
  }

  toggleOverviewPicker(event?: Event): void {
    event?.stopPropagation();
    this.overviewPickerOpen = !this.overviewPickerOpen;
    if (this.overviewPickerOpen) {
      this.overviewPickerView = 'month';
      this.syncPickerFromSelectedMonth();
      this.alignYearPageToPicker();
    }
  }

  get yearPageYears(): number[] {
    return Array.from({ length: 12 }, (_, index) => this.yearPageStart + index);
  }

  get yearPageEnd(): number {
    return this.yearPageStart + 11;
  }

  showYearPickerView(): void {
    this.overviewPickerView = 'year';
    this.alignYearPageToPicker();
  }

  prevYearPage(): void {
    this.yearPageStart -= 12;
  }

  nextYearPage(): void {
    this.yearPageStart += 12;
  }

  selectPickerYear(year: number): void {
    this.pickerYear = Number(year);
    this.overviewPickerView = 'month';
    this.applyPickerSelection(false);
  }

  selectPickerMonth(month: number): void {
    this.pickerMonth = Number(month);
    this.applyPickerSelection(true);
  }

  private alignYearPageToPicker(): void {
    this.yearPageStart = this.pickerYear - 5;
  }

  private syncPickerFromSelectedMonth(): void {
    this.pickerMonth = this.selectedMonth.month();
    this.pickerYear = this.selectedMonth.year();
    this.alignYearPageToPicker();
  }

  private applyPickerSelection(closePanel: boolean): void {
    const picked = moment({ year: this.pickerYear, month: this.pickerMonth, day: 1 });
    if (!picked.isValid()) {
      return;
    }
    const monthChanged = !picked.clone().startOf('month').isSame(this.selectedMonth, 'month');
    this.selectedMonth = picked.clone().startOf('month');
    this.syncMonthLabel();
    if (closePanel) {
      this.overviewPickerOpen = false;
    }
    if (monthChanged && this.viewMode === 'monthly') {
      this.buildMonthlyColumnDefs();
      this.loadMonthlyReport();
    }
  }

  private syncMonthLabel(): void {
    this.monthLabel = this.selectedMonth.format('MMM YYYY');
  }

  onRowClicked(event: any) {
    if (this.viewMode === 'monthly') {
      return;
    }

    const { messBuildingId, daysheetDate, messBuildingName, holiday, holidayReason, status } = event.data;
    const queryParams: Record<string, string | number | boolean> = {
      messBuildingId,
      daysheetDate,
      messBuildingName,
      holiday,
      holidayReason,
    };
    if (status != null && String(status).trim() !== '') {
      queryParams['status'] = status;
    }
    this.openDoDetailView(queryParams);
  }

  /**
   * List → detail: on MessDetails shell use setView('messDoDetailView');
   * dedicated /messDoDetails route keeps router navigation (existing feature).
   */
  private openDoDetailView(queryParams: Record<string, string | number | boolean>): void {
    const normalized: Record<string, string | number | boolean> = { ...queryParams };
    if (normalized['holiday'] === true || normalized['holiday'] === 'true') {
      normalized['holiday'] = 'true';
    } else {
      normalized['holiday'] = 'false';
    }

    if (this.router.url.includes('MessDetails')) {
      this.service.setDoDetailViewParams(normalized);
      this.service.setView('messDoDetailView');
      return;
    }

    this.router.navigate(['/messDoDetailsView'], { queryParams: normalized });
  }

  /** Rounded status dot centered inside each day cell (monthly view only). */
  private monthlyDayStatusCellRenderer = (params: ICellRendererParams): HTMLElement => {
    const wrap = document.createElement('div');
    wrap.className = 'monthly-day-dot-wrap';
    const dot = document.createElement('span');
    dot.className = `monthly-day-dot monthly-day-dot--${this.statusToDotModifier(String(params.value ?? ''))}`;
    dot.setAttribute('role', 'presentation');
    wrap.appendChild(dot);
    return wrap;
  };

  private statusToDotModifier(statusKey: string): string {
    switch (statusKey) {
      case 'CLOSED':
        return 'closed';
      case 'PENDING':
        return 'pending';
      case 'HOLIDAY':
        return 'holiday';
      case 'NOT_SUBMITTED':
        return 'not-submitted';
      default:
        return 'empty';
    }
  }

  onCellClicked(event: CellClickedEvent): void {
    if (this.viewMode !== 'monthly') {
      return;
    }
    this.navigateFromMonthlyDayCell(event);
  }

  /** Month view: click a day status cell → open mess DO detail for that building + date. */
  private navigateFromMonthlyDayCell(event: CellClickedEvent): void {
    const field = String(event.colDef?.field ?? '');
    if (!field.startsWith('day_')) {
      return;
    }

    const statusKey = String(event.data?.[field] ?? '').trim();
    if (!statusKey) {
      return;
    }

    if (statusKey === 'NOT_SUBMITTED') {
      this.openNotSubmittedDialog();
      return;
    }
    else if (statusKey === 'HOLIDAY') {
      const holidayCtx = this.getHolidayContextFromMonthlyCell(event, field);
      if (holidayCtx) {
        this.openHolidayDialog(holidayCtx);
      }
      return;
    }

    const meta = event.data?.[`${field}_meta`] as Record<string, unknown> | undefined;
    const day = Number(field.replace('day_', ''));
    const year = this.selectedMonth.year();
    const monthIndex = this.selectedMonth.month();
    const daysheetDate =
      String(meta?.['daysheetDate'] ?? '').trim() ||
      moment({ year, month: monthIndex, day }).format('DD MMM YYYY');
    const messBuildingId = meta?.['messBuildingId'] ?? event.data?.messBuildingId;
    const messBuildingName = String(meta?.['messBuildingName'] ?? event.data?.messBuildingName ?? '').trim();
    const status = String(meta?.['status'] ?? '').trim();
    const isHoliday =
      meta?.['holiday'] === true || statusKey === 'HOLIDAY';
    const holidayReason = meta?.['holidayReason'];

    const queryParams: Record<string, string | number> = {
      messBuildingId: Number(messBuildingId),
      daysheetDate,
      messBuildingName,
      status,
      holiday: isHoliday ? 'true' : 'false',
    };
    if (holidayReason != null && String(holidayReason).trim() !== '') {
      queryParams['holidayReason'] = String(holidayReason);
    }

    this.openDoDetailView(queryParams);
  }

  private openNotSubmittedDialog(): void {
    this.matDialog.open(NotSubmittedViewDialogComponent, {
      width: '360px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'not-submitted-dialog-panel',
    });
  }

  /** Context aligned with navigate queryParams: building, date, name, holiday, reason. */
  private getHolidayContextFromMonthlyCell(
    event: CellClickedEvent,
    field: string,
  ): {
    messBuildingId: unknown;
    daysheetDate: string;
    messBuildingName: string;
    holiday: boolean;
    holidayReason: string;
  } | null {
    const meta = event.data?.[`${field}_meta`] as Record<string, unknown> | undefined;
    const day = Number(field.replace('day_', ''));
    const year = this.selectedMonth.year();
    const monthIndex = this.selectedMonth.month();
    const daysheetDate =
      String(meta?.['daysheetDate'] ?? '').trim() ||
      moment({ year, month: monthIndex, day }).format('DD MMM YYYY');
    const messBuildingId = meta?.['messBuildingId'] ?? event.data?.messBuildingId;
    if (messBuildingId == null || messBuildingId === '') {
      return null;
    }
    const messBuildingName = String(meta?.['messBuildingName'] ?? event.data?.messBuildingName ?? '').trim();
    const holidayReason = String(meta?.['holidayReason'] ?? '').trim();
    return {
      messBuildingId,
      daysheetDate,
      messBuildingName,
      holiday: true,
      holidayReason,
    };
  }

  private buildHolidayClosePayload(ctx: {
    messBuildingId: unknown;
    daysheetDate: string;
  }): MessHolidayClosePayload {
    return {
      buildingId: String(ctx.messBuildingId ?? ''),
      inventoryDate: ctx.daysheetDate,
      studentCount: 0,
      employeeCount: 0,
      subStaffCount: 0,
      visitorsCount: 0,
      status: 'CLOSED',
      daysheetAmount: 0,
      scenarioBasedMenuItemsList: [],
      supplyBuildings: [],
    };
  }

  private openHolidayDialog(ctx: {
    messBuildingId: unknown;
    daysheetDate: string;
    messBuildingName: string;
    holiday: boolean;
    holidayReason: string;
  }): void {
    const dialogData: HolidayCloseDialogData = {
      daysheetDate: ctx.daysheetDate,
      holidayReason: ctx.holidayReason,
      closePayload: this.buildHolidayClosePayload(ctx),
    };
    const dialogRef = this.matDialog.open(HolidayCloseDialogComponent, {
      width: '360px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'not-submitted-dialog-panel',
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe((closed: boolean) => {
      if (closed && this.viewMode === 'monthly') {
        this.loadMonthlyReport();
      }
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    if (this.viewMode === 'monthly') {
      setTimeout(() => this.rebuildMonthlyColumnsToFitViewport(), 150);
    }
  }

  onfilterChange(event: any) {
    this.quickFilter = event.target.value;
  }

  focusSearchInput() {
    setTimeout(() => {
      if (this.searchInputcampus) {
        this.searchInputcampus.nativeElement.focus();
      }
    }, 100);
  }

  colDefs: ColDef[] = [
    {
      headerName: 'S.No',
      pinned: 'left',
      width: 70,
      cellStyle: { 'text-align': 'center', 'font-size': '16px' },
      valueGetter: (params: any) => params.node.rowIndex + 1,
    },
    {
      field: 'messBuildingName',
      headerName: 'Kitchen Name',
      pinned: 'left',
      width: 300,
      cellStyle: { 'text-align': 'left', 'font-size': '16px' },
    },
    {
      field: 'daysheetDate',
      headerName: 'Daysheet Date',
      cellStyle: { 'text-align': 'left', 'font-size': '16px' },
    },
    {
      field: 'studentCount',
      headerName: 'Student Count',
      cellStyle: { 'text-align': 'right', 'font-size': '16px' },
    },
    {
      field: 'employeeCount',
      headerName: 'Employee Count',
      cellStyle: { 'text-align': 'right', 'font-size': '16px' },
    },
    {
      field: 'subStaffCount',
      headerName: 'SubStaff Count',
      cellStyle: { 'text-align': 'right', 'font-size': '16px' },
    },
    {
      field: 'visitorsCount',
      headerName: 'visitors Count',
      cellStyle: { 'text-align': 'right', 'font-size': '16px' },
    },
    {
      field: 'status',
      headerName: 'Status',
      cellStyle: { 'text-align': 'left', 'font-size': '16px' },
    },
  ];

  rowData: PeriodicElement2[] = [];

  defaultColDef: ColDef = {
    flex: 1,
    menuTabs: [],
    suppressHeaderMenuButton: true,
    sortable: true,
    filter: false,
  };

  overlayLoadingTemplate = `
    <span class="ag-overlay-loading-center">
      Please wait while data is loading...
    </span>`;

  overlayNoRowsTemplate = `
    <span>No data found</span>`;

  pendingList() {
    this.rowData = [];
    this.gridApi?.setGridOption('rowData', []);
    this.gridApi?.showLoadingOverlay();
    this.spinner.show();
    this.service.getdouReport().subscribe({
      next: (res: any) => {
        this.spinner.hide();
        if (!res || res === 204) {
          this.rowData = [];
          this.gridApi?.showNoRowsOverlay();
          return;
        }
        this.rowData = Array.isArray(res) ? res : [];
        if (!this.rowData.length) {
          this.gridApi?.showNoRowsOverlay();
        } else {
          this.gridApi?.hideOverlay();
        }
      },
      error: () => {
        this.rowData = [];
        this.spinner.hide();
        this.gridApi?.showNoRowsOverlay();
      },
    });
  }

  loadMonthlyReport(): void {
    const month = this.selectedMonth.month() + 1;
    const year = this.selectedMonth.year();
    this.buildMonthlyColumnDefs();
    this.rowData = [];
    this.gridApi?.setGridOption('rowData', []);
    this.gridApi?.showLoadingOverlay();
    this.spinner.show();
    this.service.monthlyDouReport(month, year).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res ?? {};
        const monthText = String(data.month ?? this.selectedMonth.format('MMM YYYY'));
        this.syncSelectedMonthFromReport(monthText);
        this.monthLabel = monthText;
        this.buildMonthlyColumnDefs();
        const summary = data.summary ?? {};
        const nextSummary: MonthlySummary = {
          closed: Number(summary.closed ?? 0),
          pending: Number(summary.pending ?? 0),
          holiday: Number(summary.holiday ?? 0),
          notSubmitted: Number(summary.notSubmitted ?? 0),
        };
        const buildings = Array.isArray(data.buildings) ? data.buildings : [];
        const nextTotalCampuses = buildings.length;
        this.monthlySummary = nextSummary;
        this.totalCampuses = nextTotalCampuses;
        this.updateSummaryTrendsFromResponse(nextSummary, nextTotalCampuses);
        this.rowData = buildings.map((b: any) => this.mapBuildingToMonthlyRow(b));
        this.spinner.hide();
        if (!this.rowData.length) {
          this.gridApi?.showNoRowsOverlay();
        } else {
          this.gridApi?.hideOverlay();
        }
        this.applyMonthlyGridLayout();
      },
      error: () => {
        this.rowData = [];
        this.monthlySummary = { closed: 0, pending: 0, holiday: 0, notSubmitted: 0 };
        this.totalCampuses = 0;
        this.updateSummaryTrendsFromResponse(this.monthlySummary, 0);
        this.spinner.hide();
        this.gridApi?.showNoRowsOverlay();
      },
    });
  }

  /** Day numbers for monthly grid: newest first (e.g. 5, 4, 3, 2, 1 for current month on day 5). */
  private getMonthlyVisibleDays(): number[] {
    const daysInMonth = this.selectedMonth.daysInMonth();
    const today = moment();
    const isCurrentMonth = this.selectedMonth.isSame(today, 'month');
    const lastDay = isCurrentMonth ? Math.min(today.date(), daysInMonth) : daysInMonth;
    const days: number[] = [];
    for (let d = lastDay; d >= 1; d--) {
      days.push(d);
    }
    return days;
  }

  private buildMonthlyColumnDefs(): void {
    const year = this.selectedMonth.year();
    const monthIndex = this.selectedMonth.month();
    this.monthlyDayCount = this.selectedMonth.daysInMonth();
    const visibleDays = this.getMonthlyVisibleDays();
    this.monthlyVisibleDayCount = visibleDays.length;
    const dayCols: ColDef[] = [];
    const campusWidth = this.getMonthlyCampusWidth();

    for (const d of visibleDays) {
      const field = `day_${d}`;
      const dow = moment({ year, month: monthIndex, day: d }).format('ddd');
      dayCols.push({
        field,
        headerName: `${d}\n${dow}`,
        flex: 1,
        minWidth: this.monthlyDayMinWidth,
        sortable: false,
        suppressHeaderMenuButton: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        headerClass: 'monthly-day-header',
        cellClass: 'monthly-day-cell',
        cellStyle: this.monthlyDayCellStyle,
        cellRenderer: this.monthlyDayStatusCellRenderer,
        valueFormatter: () => '',
      });
    }

    this.monthlyColDefs = [
      {
        field: 'messBuildingName',
        headerName: 'Kitchen Name',
        pinned: 'left',
        width: campusWidth,
        minWidth: this.monthlyCampusMinWidth,
        maxWidth: this.monthlyCampusMaxWidth,
        flex: 0,
        resizable: true,
        cellClass: 'monthly-campus-cell',
        tooltipField: 'messBuildingName',
        cellStyle: { 'text-align': 'left', 'font-size': '13px' },
      },
      ...dayCols,
      {
        field: 'submittedSummary',
        headerName: 'Status',
        pinned: 'right',
        width: this.monthlySummaryWidth,
        minWidth: this.monthlySummaryWidth,
        maxWidth: this.monthlySummaryWidth,
        flex: 0,
        sortable: false,
        tooltipField: 'submittedSummary',
        cellStyle: { 'text-align': 'center', 'font-weight': '600', 'font-size': '12px' },
      },
    ];
  }

  private mapBuildingToMonthlyRow(building: any): Record<string, unknown> {
    const year = this.selectedMonth.year();
    const monthIndex = this.selectedMonth.month();
    const dailyStatus: any[] = Array.isArray(building?.dailyStatus) ? building.dailyStatus : [];
    const row: Record<string, unknown> = {
      messBuildingId: building.messBuildingId,
      messBuildingName: building.messBuildingName,
    };
    const visibleDays = this.getMonthlyVisibleDays();
    let closedCount = 0;

    for (let d = 1; d <= this.monthlyDayCount; d++) {
      const field = `day_${d}`;
      const entry = dailyStatus.find((x: any) => this.isSameDaysheetDate(x?.daysheetDate, year, monthIndex, d));
      if (entry) {
        const statusKey = this.resolveDayStatusKey(entry);
        row[field] = statusKey;
        row[`${field}_meta`] = entry;
        if (visibleDays.includes(d) && statusKey === 'CLOSED') {
          closedCount++;
        }
      } else {
        row[field] = '';
      }
    }

    const visibleTotal = visibleDays.length || this.monthlyDayCount;
    row['submittedSummary'] = `${closedCount}/${visibleTotal}`;
    return row;
  }

  private isSameDaysheetDate(value: unknown, year: number, monthIndex: number, day: number): boolean {
    const parsed = moment(String(value ?? '').trim(), ['DD MMM YYYY', 'D MMM YYYY'], true);
    if (!parsed.isValid()) {
      return false;
    }
    return parsed.year() === year && parsed.month() === monthIndex && parsed.date() === day;
  }

  private resolveDayStatusKey(entry: any): string {
    const status = String(entry?.status ?? 'NOT_SUBMITTED').trim();
    const normalized = status.toUpperCase();
    if (normalized === 'CLOSED' || normalized === 'CLOSE') {
      return 'CLOSED';
    }
    if (entry?.holiday) {
      return 'HOLIDAY';
    }
    return status || 'NOT_SUBMITTED';
  }

  /** Neutral cell chrome; status color is on the round dot inside the renderer. */
  private monthlyDayCellStyle = (): Record<string, string> => ({
    padding: '5px 3px',
    border: '1px solid #eef2f7',
    background: '#fafbfc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });
}
